import { AutomationScheduleFields } from "@/panes/automation/AutomationScheduleFields";
import { formatDayTime } from "@/panes/automation/cadence";
import {
  defaultScheduleDraft,
  draftFromSchedule,
  usesStructuredEditor,
  type ScheduleDraft,
} from "@/panes/automation/scheduleDraft";
import { type FlowSchedule } from "@cohesive/api-client";
import { forwardRef } from "react";

/**
 * The schedule section of the automation pane, controlled by the pane's draft
 * so nothing is written until Save.
 *
 * `value === null` means "leave the stored cadence alone" — the state a custom
 * cron or a pending one-off sits in, since neither can be represented by the
 * structured controls. Replacing one hands the parent a real draft; those are
 * the only two shapes that start read-only.
 */
export const AutomationScheduleEditor = forwardRef<
  HTMLDivElement,
  {
    schedule: FlowSchedule;
    value: ScheduleDraft | null;
    onChange: (draft: ScheduleDraft | null) => void;
    disabled?: boolean;
  }
>(function AutomationScheduleEditor(
  { schedule, value, onChange, disabled },
  ref,
) {
  const structured = usesStructuredEditor(schedule);
  const timeZone = draftFromSchedule(schedule).timeZone;

  const replace = () => onChange(defaultScheduleDraft({ timeZone }));
  const keepStored = () => onChange(null);

  return (
    <div ref={ref} className="flex flex-col gap-2">
      {value !== null ? (
        <>
          <AutomationScheduleFields
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
          {!structured && (
            <button
              type="button"
              onClick={keepStored}
              className="self-start text-xs text-n-50 transition-colors hover:text-n-80"
            >
              Keep the current schedule
            </button>
          )}
        </>
      ) : schedule.cron ? (
        <div className="flex flex-col gap-1.5 rounded-md border px-2.5 py-2">
          <p className="text-xs font-medium text-n-70">
            Custom schedule (cron)
          </p>
          <code className="font-mono text-xs text-n-80">{schedule.cron}</code>
          {schedule.cron_time_zone && (
            <p className="text-xs text-n-40">{schedule.cron_time_zone}</p>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={replace}
              className="self-start text-xs text-n-50 transition-colors hover:text-n-80"
            >
              Replace with a standard schedule
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5 rounded-md border px-2.5 py-2">
          <p className="text-sm text-n-80">
            {schedule.next_run_at
              ? `Runs once on ${formatDayTime(schedule.next_run_at)}`
              : "Ran once."}
          </p>
          {!disabled && (
            <button
              type="button"
              onClick={replace}
              className="self-start text-xs text-n-50 transition-colors hover:text-n-80"
            >
              Replace with a repeating schedule
            </button>
          )}
        </div>
      )}

      {schedule.next_run_at && (
        <p className="text-xs text-n-60">
          Next run: {formatDayTime(schedule.next_run_at)}
        </p>
      )}
      {value !== null && !disabled && (
        <p className="text-xs text-n-40">
          Saving a schedule change activates the automation.
        </p>
      )}
    </div>
  );
});
